import {
  AlertTriangle,
  ChevronDown,
  CircleCheckBigIcon,
  CircleUser,
  Lock,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { PopoverClose } from "@radix-ui/react-popover";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useLogoutMutation } from "@/redux/slices/auth/authApiSlice";
import oktaService from "@/services/OktaService";
import { backendApiUrl } from "@/lib/config";
import { mockLogout } from "@/services/mockAuth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useState } from "react";
import { showErrorToast, showSuccessToast } from "./toast/toast";

export default function Profile({
  email,
  displayName,
  role,
}: {
  email: string | undefined;
  displayName?: string;
  role: "user" | "admin" | "manager";
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logout] = useLogoutMutation();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  async function Logout() {
    try {
      mockLogout();
      // Call backend logout endpoint
      // await logout({}).unwrap();

      // Use Okta service to handle logout
      await oktaService.logout(true,true);
      navigate('/login');
      // Redirect will be handled by Okta service
    } catch (e) {
      console.error('Logout error:', e);
      // If Okta logout fails, redirect manually
      navigate('/login');
    }
  }

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      showErrorToast("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      // Get the current Okta user to access the token
      const oktaUser = await oktaService.getUser();

      const response = await fetch(`${backendApiUrl}/users/changePassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${oktaUser?.access_token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to change password');
      }

      showSuccessToast('Password changed successfully');
      setShowPasswordDialog(false);
      setCurrentPassword("");
      setNewPassword("");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change password';
      console.log(error, 'change pass error');
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and new password to update.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Current Password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPasswordDialog(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Update Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Popover>
        <PopoverTrigger className="hover:no-underline border-none">
          <div className="flex-center gap-1">
            <CircleUser color="#fff" strokeWidth={1.5} size={30} />
            &nbsp;
            <p className="hidden sm:block semi-bold-16 text-sm lg:text-base text-white">
              {displayName}
            </p>
            <ChevronDown
              className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down data-[state=open]:text-states-error"
              color="#fff"
              size={20}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="bg-white profile-shadow p-0 mr-5 w-fit">
          <div className="w-full flex gap-2 items-center pl-5 py-5">
            <CircleUser className="hidden sm:block" strokeWidth={1.2} size={40} />
            <div className="font-medium w-full">
              <h3 className="capitalize text-md semi-bold-16 text-left text-[#414042] ">
                {displayName}
              </h3>
              <p className="text-[12px]  text-[#90A0B7]">{email}</p>
            </div>
          </div>

          <PopoverClose className="text-left w-full">
            {user?.role === 'admin' &&
              <Link to={`/admin`}>
                <p className="profile-item">
                  Admin Panel
                </p>
              </Link>
            }
            <div className="flex items-center gap-2">

              {/* <p
                className="profile-item"
                onClick={() => setShowPasswordDialog(true)}
              >
               
                Change Password
              </p> */}
            </div>
            <p className="profile-item-red" onClick={() => Logout()}>
              Log Out
            </p>
          </PopoverClose>
        </PopoverContent>
      </Popover>
    </>
  );
}
